import { Button, ButtonColors } from '@chiroup/components/Button';
import { Visit } from '@chiroup/core/types/PatientVisit.type';
import React from 'react';
import { Regions } from '../../../../../constants';
import ROM from './ROM';

type Props = {
  rows?: any[];
  onChange: (
    tab: string,
    key: string,
    val: {
      value?: number;
      pain?: boolean;
      side?: 'Right' | 'Left' | 'Both' | 'SpinalArea';
    }
  ) => void;
  currentTab: string;
  value: {
    [key: string]: {
      [key: string]: {
        [key: string]: { value?: number; pain?: boolean };
      };
    };
  };
  setVisitForm: React.Dispatch<React.SetStateAction<Visit>>;
  complaintId: string;
  romSliderStep: number;
  signed?: boolean;
};

const RangeOfMotionRegion: React.FC<Props> = ({
  rows = [],
  onChange,
  currentTab,
  value,
  setVisitForm,
  complaintId,
  romSliderStep,
  signed = false,
}) => {
  const spinalRegion =
    currentTab === Regions.C || currentTab === Regions.L || currentTab === Regions.T;

  const allNormal = () => {
    setVisitForm(prev => {
      const newVal = JSON.parse(JSON.stringify(prev));

      newVal.note = newVal.note || {};
      newVal.note[complaintId] = newVal.note[complaintId] || {};
      newVal.note[complaintId].objective = newVal.note[complaintId].objective || {};
      newVal.note[complaintId].objective.RangeOfMotion =
        newVal.note[complaintId].objective.RangeOfMotion || {};
      newVal.note[complaintId].objective.RangeOfMotion.detail =
        newVal.note[complaintId].objective.RangeOfMotion.detail || {};
      newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab] =
        newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab] || {};

      rows.forEach(rom => {
        if (currentTab === Regions.C || currentTab === Regions.L || currentTab === Regions.T) {
          newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab][rom.value] = {
            SpinalArea: {
              value: rom.normal,
              pain: false,
            },
          };
        } else {
          newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab][rom.value] =
            newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab][rom.value] || {};
          newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab][rom.value].Left = {
            value: rom.normal,
            pain: false,
          };
          newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab][rom.value].Right = {
            value: rom.normal,
            pain: false,
          };
        }

        newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab] = {
          ...newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab],
          touched: true,
        };
      });

      return newVal;
    });
  };

  const clearAll = () => {
    setVisitForm(prev => {
      const newVal = JSON.parse(JSON.stringify(prev));

      delete newVal?.note?.[complaintId]?.objective?.RangeOfMotion?.detail?.[currentTab];

      return newVal;
    });
  };

  const getValue = ({
    currentValue,
    percent,
    normal,
    max,
    min,
  }: {
    currentValue: number;
    percent: number;
    normal: number;
    max: number;
    min: number;
  }) => {
    // use percent of normal unless normal is zero, then use percent of bound
    let changeAmount;
    let rawValue;

    if (normal !== 0) {
      // percent is applied to normal value
      changeAmount = normal * (percent / 100);
      rawValue = (currentValue ?? normal) + changeAmount;
    } else {
      // percent is applied as a symmetric percent of the largest distance from normal to a bound
      const positiveDistance = Math.abs(max - normal);
      const negativeDistance = Math.abs(min - normal);
      const symmetricDistance = Math.max(positiveDistance, negativeDistance);
      changeAmount = symmetricDistance * (percent / 100);
      rawValue = (currentValue ?? 0) + changeAmount;
    }

    let value;

    // snap to nearest multiple of romSliderStep in the direction of the change
    // (keeps change amounts consistent whether negative or positive)
    if (changeAmount > 0) {
      value = Math.floor(rawValue / romSliderStep) * romSliderStep;
    } else if (changeAmount < 0) {
      value = Math.ceil(rawValue / romSliderStep) * romSliderStep;
    } else {
      value = Math.round(rawValue / romSliderStep) * romSliderStep;
    }

    if (currentValue === value) {
      // add or subtract to the next highest or lowest multiple of romSliderStep
      if (changeAmount > 0) {
        value = currentValue + romSliderStep;
      } else if (changeAmount < 0) {
        value = currentValue - romSliderStep;
      }
    }

    if (value > max) {
      value = max;
    }

    if (value < min) {
      value = min;
    }

    return value;
  };

  const percentChange = (percent: number) => {
    setVisitForm(prev => {
      const newVal = JSON.parse(JSON.stringify(prev));

      newVal.note = newVal?.note || {};
      newVal.note[complaintId] = newVal?.note?.[complaintId] || {};
      newVal.note[complaintId].objective = newVal?.note?.[complaintId]?.objective || {};
      newVal.note[complaintId].objective.RangeOfMotion =
        newVal?.note?.[complaintId]?.objective?.RangeOfMotion || {};
      newVal.note[complaintId].objective.RangeOfMotion.detail =
        newVal?.note?.[complaintId]?.objective?.RangeOfMotion?.detail || {};
      newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab] =
        newVal?.note?.[complaintId]?.objective?.RangeOfMotion?.detail?.[currentTab] || {};

      const spinalArea =
        currentTab === Regions.C || currentTab === Regions.L || currentTab === Regions.T;

      rows.forEach(rom => {
        if (spinalArea) {
          const currentRomValue = getValue({
            currentValue:
              newVal?.note?.[complaintId]?.objective?.RangeOfMotion?.detail?.[currentTab]?.[
                rom.value
              ]?.SpinalArea?.value,
            percent: percent,
            normal: rom.normal,
            max: rom.max,
            min: rom.min,
          });

          newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab][rom.value] = {
            SpinalArea: {
              value: currentRomValue,
              pain:
                newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab][rom.value]
                  ?.SpinalArea?.pain || false,
            },
          };
        } else {
          newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab][rom.value] =
            newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab][rom.value] || {};

          const currentLeftValue = getValue({
            currentValue:
              newVal?.note?.[complaintId]?.objective?.RangeOfMotion?.detail?.[currentTab]?.[
                rom.value
              ]?.Left?.value,
            percent,
            normal: rom.normal,
            max: rom.max,
            min: rom.min,
          });

          const currentRightValue = getValue({
            currentValue:
              newVal?.note?.[complaintId]?.objective?.RangeOfMotion?.detail?.[currentTab]?.[
                rom.value
              ]?.Right?.value,
            percent,
            normal: rom.normal,
            max: rom.max,
            min: rom.min,
          });

          newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab][rom.value].Left = {
            value: currentLeftValue,
            pain:
              newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab][rom.value]?.Left
                ?.pain || false,
          };
          newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab][rom.value].Right = {
            value: currentRightValue,
            pain: newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab][rom.value]
              ?.Right?.pain,
          };
        }

        newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab] = {
          ...newVal.note[complaintId].objective.RangeOfMotion.detail[currentTab],
          touched: true,
        };
      });

      return newVal;
    });
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 justify-between my-4">
        <div>
          <Button
            text="All normal"
            color={ButtonColors.plainWithBorder}
            onClick={allNormal}
            className="mr-2"
            disabled={signed}
          />
          {!!value?.[currentTab] && (
            <Button
              text="Clear all"
              color={ButtonColors.plainWithBorder}
              onClick={clearAll}
              disabled={signed}
            />
          )}
        </div>
        <div className="ml-2">
          <Button
            text="-25%"
            color={ButtonColors.plainWithBorder}
            onClick={() => percentChange(-25)}
            className="mr-2"
            disabled={signed}
          />
          <Button
            text="-5%"
            color={ButtonColors.plainWithBorder}
            onClick={() => percentChange(-5)}
            className="mr-2"
            disabled={signed}
          />
          <Button
            text="+5%"
            color={ButtonColors.plainWithBorder}
            onClick={() => percentChange(5)}
            className="mr-2"
            disabled={signed}
          />
          <Button
            text="+25%"
            color={ButtonColors.plainWithBorder}
            onClick={() => percentChange(25)}
            disabled={signed}
          />
        </div>
      </div>
      {!spinalRegion ? (
        <div className="flex flex-col 2xl:flex-row gap-4 min-w-0">
          {' '}
          <ROM
            rows={rows}
            value={value}
            onChange={onChange}
            currentTab={currentTab}
            side="Left"
            showSide={true}
            romSliderStep={romSliderStep}
          />{' '}
          <ROM
            rows={rows}
            value={value}
            onChange={onChange}
            currentTab={currentTab}
            side="Right"
            showSide={true}
            romSliderStep={romSliderStep}
          />
        </div>
      ) : (
        <ROM
          rows={rows}
          value={value}
          onChange={onChange}
          currentTab={currentTab}
          side={!spinalRegion ? 'Both' : 'SpinalArea'}
          romSliderStep={romSliderStep}
        />
      )}
    </>
  );
};

export default RangeOfMotionRegion;
